import { Lead, normalizeLeadTruckDropoffLocationIds } from '@otw/models/leads/lead';
import { Vehicle } from '@otw/models/inventory/inventory';
import type { Dispatch, SetStateAction } from 'react';

/**
 * Attaches a vehicle to a lead by updating the lead's truckIds array
 * 
 * @param vehicle - The vehicle to attach
 * @param lead - The lead to attach the vehicle to
 * @param attachedVehicleIds - Current array of attached vehicle IDs
 * @param updateLead - Function to update the lead in the database
 * @param setAttachingVehicle - State setter for tracking attachment status
 * @param setAttachedVehicleIds - State setter for updating local vehicle IDs
 * @returns Promise<string[]>
 */
export const attachVehicleToLead = async (
  vehicle: Vehicle,
  lead: Lead,
  attachedVehicleIds: string[],
  updateLead: (params: { id: string; updates: Partial<Lead>; statusChangeNote?: string }) => Promise<any>,
  setAttachingVehicle: (id: string | null) => void,
  setAttachedVehicleIds: (ids: string[]) => void
) => {
  try {
    setAttachingVehicle(vehicle.id);
    
    // Check if vehicle is already attached
    if (attachedVehicleIds.includes(vehicle.id)) {
      // Already attached, no need to update
      setAttachingVehicle(null);
      return [];
    }
    
    // Add vehicle ID to lead's truckIds
    const updatedTruckIds = [...attachedVehicleIds, vehicle.id];
    
    console.log('Attaching vehicle to lead:', {
      leadId: lead.id,
      vehicleId: vehicle.id,
      updatedTruckIds
    });
    
    // Update the lead in the database
    await updateLead({
      id: lead.id,
      updates: {
        truckIds: updatedTruckIds
      }
    });
    
    // Update local state to reflect the change immediately
    setAttachedVehicleIds(updatedTruckIds);
    
    return updatedTruckIds;
  } catch (error) {
    console.error('Error attaching vehicle to lead:', error);
    return null;
  } finally {
    setAttachingVehicle(null);
  }
};

/**
 * Removes a vehicle from a lead by updating the lead's truckIds array
 * 
 * @param vehicle - The vehicle to remove
 * @param lead - The lead to remove the vehicle from
 * @param attachedVehicleIds - Current array of attached vehicle IDs
 * @param updateLead - Function to update the lead in the database
 * @param setAttachingVehicle - State setter for tracking attachment status
 * @param setAttachedVehicleIds - State setter for updating local vehicle IDs
 * @param setLinkedVehicles - Optional state setter for updating linked vehicles display
 * @returns Promise<string[]>
 */
export const removeVehicleFromLead = async (
  vehicle: Vehicle,
  lead: Lead,
  attachedVehicleIds: string[],
  updateLead: (params: { id: string; updates: Partial<Lead>; statusChangeNote?: string }) => Promise<any>,
  setAttachingVehicle: (id: string | null) => void,
  setAttachedVehicleIds: (ids: string[]) => void,
  setLinkedVehicles?: Dispatch<SetStateAction<Vehicle[]>>
) => {
  try {
    setAttachingVehicle(vehicle.id);
    
    // Filter out the vehicle ID from the lead's truckIds
    const updatedTruckIds = attachedVehicleIds.filter(id => id !== vehicle.id);

    const truckDropoffLocationIds = { ...normalizeLeadTruckDropoffLocationIds(lead.truckDropoffLocationIds) };
    delete truckDropoffLocationIds[vehicle.id];
    
    // Update the lead in the database
    await updateLead({
      id: lead.id,
      updates: {
        truckIds: updatedTruckIds,
        truckDropoffLocationIds,
      }
    });
    
    // Update local state to reflect the change immediately
    setAttachedVehicleIds(updatedTruckIds);
    
    // Update linkedVehicles state if provided
    if (setLinkedVehicles) {
      setLinkedVehicles(prev => prev.filter(v => v.id !== vehicle.id));
    }
    
    console.log('Vehicle removed successfully:', vehicle.id);
    
    return updatedTruckIds;
  } catch (error) {
    console.error('Error removing vehicle from lead:', error);
    return null;
  } finally {
    setAttachingVehicle(null);
  }
}; 